import { useCallback, useRef } from 'react';
import Highcharts from 'highcharts';

/**
 * Hook that synchronizes crosshairs, tooltips, and zoom across multiple Highcharts charts.
 * All charts share the same X-axis (Horizontal Displacement), so syncing is natural.
 */
export function useSyncedCharts() {
  const chartsRef = useRef<(Highcharts.Chart | null)[]>([]);
  const syncingRef = useRef(false);
  const zoomedRef = useRef(false);

  const registerChart = useCallback((index: number) => {
    return (ref: { chart: Highcharts.Chart } | null) => {
      chartsRef.current[index] = ref?.chart ?? null;
    };
  }, []);

  /** Destroy all custom reset buttons on every chart. */
  const destroyResetButtons = useCallback(() => {
    for (const chart of chartsRef.current) {
      if (!chart) continue;
      const c = chart as unknown as Record<string, unknown>;
      const btn = c._syncResetBtn as Highcharts.SVGElement | undefined;
      if (btn) { btn.destroy(); c._syncResetBtn = undefined; }
    }
  }, []);

  /** Show a custom "Reset zoom" button on every chart. */
  const showResetButtons = useCallback(() => {
    for (const chart of chartsRef.current) {
      if (!chart) continue;
      const c = chart as unknown as Record<string, unknown>;
      // Don't duplicate
      if (c._syncResetBtn) continue;

      const btn = chart.renderer
        .button('Reset zoom', 0, 0, () => resetAllZoom(), undefined, undefined, undefined)
        .attr({
          zIndex: 8,
          padding: 5,
        })
        .css({
          fontSize: '11px',
          fontWeight: '400',
          color: '#333',
        })
        .add();

      // Position in upper-right of plot area
      const x = chart.plotLeft + chart.plotWidth - (btn.getBBox().width) - 8;
      const y = chart.plotTop + 8;
      btn.attr({ x, y });

      c._syncResetBtn = btn;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Reset zoom on ALL charts. */
  const resetAllZoom = useCallback(() => {
    syncingRef.current = true;
    zoomedRef.current = false;
    destroyResetButtons();
    for (const chart of chartsRef.current) {
      if (!chart) continue;
      chart.xAxis[0].setExtremes(undefined, undefined, true, false);
    }
    syncingRef.current = false;
  }, [destroyResetButtons]);

  // Wire showResetButtons to use resetAllZoom — circular dep resolved via ref
  // by having showResetButtons call resetAllZoom which is stable

  const syncCrosshair = useCallback((sourceIndex: number, e: MouseEvent) => {
    const charts = chartsRef.current;
    const sourceChart = charts[sourceIndex];
    if (!sourceChart) return;

    const sourceXAxis = sourceChart.xAxis[0];
    const xValue = sourceXAxis.toValue(
      e.clientX - (sourceChart.plotLeft + sourceChart.container.getBoundingClientRect().left),
      false,
    );

    for (let i = 0; i < charts.length; i++) {
      if (i === sourceIndex) continue;
      const chart = charts[i];
      if (!chart) continue;

      const xAxis = chart.xAxis[0];
      const point = chart.series[0]?.searchPoint(
        { chartX: xAxis.toPixels(xValue, false), chartY: 0 } as Highcharts.PointerEventObject,
        true,
      );
      if (point) {
        xAxis.drawCrosshair(undefined, point);
        chart.tooltip.refresh([point]);
      }
    }
  }, []);

  const hideCrosshairs = useCallback(() => {
    for (const chart of chartsRef.current) {
      if (!chart) continue;
      chart.xAxis[0].hideCrosshair();
      chart.tooltip.hide();
    }
  }, []);

  /** Called by afterSetExtremes on any chart — syncs zoom to all others. */
  const syncZoom = useCallback((sourceIndex: number, min: number | undefined, max: number | undefined) => {
    if (syncingRef.current) return;
    syncingRef.current = true;

    const charts = chartsRef.current;
    const sourceChart = charts[sourceIndex];
    const dataExtremes = sourceChart?.xAxis[0].getExtremes();
    const isReset = dataExtremes && min === dataExtremes.dataMin && max === dataExtremes.dataMax;

    if (isReset) {
      zoomedRef.current = false;
      destroyResetButtons();
      for (let i = 0; i < charts.length; i++) {
        if (i === sourceIndex) continue;
        const chart = charts[i];
        if (!chart) continue;
        chart.xAxis[0].setExtremes(undefined, undefined, true, false);
      }
    } else {
      for (let i = 0; i < charts.length; i++) {
        if (i === sourceIndex) continue;
        const chart = charts[i];
        if (!chart) continue;
        chart.xAxis[0].setExtremes(min, max, true, false);
      }
      if (!zoomedRef.current) {
        zoomedRef.current = true;
        // Defer so charts have rendered after setExtremes
        setTimeout(() => showResetButtons(), 50);
      }
    }

    syncingRef.current = false;
  }, [destroyResetButtons, showResetButtons]);

  return { registerChart, syncCrosshair, hideCrosshairs, syncZoom, resetAllZoom };
}
